import {
  AsyncJobDTO,
  AsyncJobStep,
  AsyncJobStepName,
  AsyncJobStepStatus,
} from '@recurrency/core-api-schema/dist/asyncJobs/common';
import { ValidationStat } from '@recurrency/core-api-schema/dist/validationRules/getValidationRules';
import moment from 'moment';

import { SparklinePoint } from 'components/Sparkline';

/** One item-location's min/max change, as recorded on the export step that carried it. */
export interface MinMaxChange {
  key: string;
  itemId: string;
  locationId: string;
  currentMin?: number;
  currentMax?: number;
  newMin: number;
  newMax: number;
  /**
   * False when the batch carrying this item-location failed. Those new values never reached the ERP,
   * so the row must not read as an exported change.
   */
  exported: boolean;
  /** Error from the batch, when it failed. */
  error?: string;
}

interface MinMaxUpdateRequest {
  item_id?: string;
  item_uid?: string;
  location_id?: string;
  current_min?: number;
  current_max?: number;
  new_min?: number;
  new_max?: number;
}

/**
 * Flattens an automated export job's batches into per-item-location changes.
 *
 * Only ExportERPMinMaxes steps carry item payloads. A batch holds up to 100 item-locations and
 * succeeds or fails as a unit, so each item inherits its batch's outcome.
 */
export function getMinMaxChanges(steps: AsyncJobStep[]): MinMaxChange[] {
  return steps
    .filter((step) => step.name === AsyncJobStepName.ExportERPMinMaxes)
    .flatMap((step) => {
      const updates: MinMaxUpdateRequest[] = step.request?.payload?.updates ?? [];
      const failed = step.status === AsyncJobStepStatus.Failed;
      return updates.map((update, index) => ({
        key: `${step.id}-${index}`,
        itemId: update.item_id ?? update.item_uid ?? '-',
        locationId: update.location_id ?? '-',
        currentMin: update.current_min,
        currentMax: update.current_max,
        newMin: update.new_min ?? 0,
        newMax: update.new_max ?? 0,
        exported: !failed,
        error: failed ? step.error : undefined,
      }));
    });
}

/**
 * Units the run added to or removed from this item-location, signed: the change in max, which is
 * what the stocking level actually moves by. A missing before value counts as no change, since
 * there is nothing to move from.
 */
export function changeImpact(change: MinMaxChange): number {
  return change.currentMax === undefined ? 0 : change.newMax - change.currentMax;
}

/** Total change across both bounds, used to surface the most consequential rows first. */
export function changeMagnitude(change: MinMaxChange): number {
  const minDelta = change.currentMin === undefined ? 0 : Math.abs(change.newMin - change.currentMin);
  const maxDelta = change.currentMax === undefined ? 0 : Math.abs(change.newMax - change.currentMax);
  return minDelta + maxDelta;
}

export function sortByLargestChange(changes: MinMaxChange[]): MinMaxChange[] {
  return [...changes].sort((a, b) => changeMagnitude(b) - changeMagnitude(a));
}

export interface RunCounts {
  exported: number;
  failed: number;
}

/**
 * Batch counts for a run, straight from the list response.
 *
 * An automated run queues exactly one ExportERPMinMaxes step per batch and no sync-back steps, so
 * its step counts are its batch counts. Item-location counts are not derivable here — only the last
 * batch of a run is a partial size — so the run list stays at batch granularity and the last-run
 * card, which loads a single job's steps anyway, is where individual item-locations are shown.
 */
export function getBatchCounts(job: AsyncJobDTO): RunCounts {
  const counts = job.stepStatusCounts;
  return {
    exported: counts?.[AsyncJobStepStatus.Success] ?? 0,
    failed: counts?.[AsyncJobStepStatus.Failed] ?? 0,
  };
}

/**
 * Exact item-location counts from a run's steps.
 *
 * Steps still pending or running are counted as neither: their item-locations have not reached the
 * ERP yet, and calling them exported would overstate a run in flight.
 */
function countItemsInStep(step: AsyncJobStep): RunCounts {
  const itemsSent = (step.request?.payload?.updates ?? []).length;

  if (step.status === AsyncJobStepStatus.Failed) return { exported: 0, failed: itemsSent };
  // pending and running steps are counted as neither: their values have not reached the ERP yet
  if (step.status !== AsyncJobStepStatus.Success) return { exported: 0, failed: 0 };

  // The exporter reports per-item outcomes for ERPs that support them (NetSuite), returning 200 with
  // a non-zero `failed`. Trusting the step status alone would report those as fully exported.
  const reportedFailed = Number(step.response?.failed ?? 0);
  if (reportedFailed > 0) {
    const reportedUpdated = Number(step.response?.updated ?? NaN);
    return {
      exported: Number.isFinite(reportedUpdated) ? reportedUpdated : itemsSent - reportedFailed,
      failed: reportedFailed,
    };
  }
  return { exported: itemsSent, failed: 0 };
}

export function countItemsFromSteps(steps: AsyncJobStep[]): RunCounts {
  return steps
    .filter((step) => step.name === AsyncJobStepName.ExportERPMinMaxes)
    .map(countItemsInStep)
    .reduce((totals, step) => ({ exported: totals.exported + step.exported, failed: totals.failed + step.failed }), {
      exported: 0,
      failed: 0,
    });
}

/** Local timezone abbreviation, e.g. `PDT`. Stated once per surface rather than on every timestamp. */
export function getLocalTimezoneAbbreviation(): string {
  return (
    new Intl.DateTimeFormat('en-US', { timeZoneName: 'short' })
      .formatToParts(new Date())
      .find((part) => part.type === 'timeZoneName')?.value || ''
  );
}

/**
 * Short axis label for a run, e.g. `Aug 8 6:04am`. Runs are nightly so the date carries the meaning;
 * the timezone is stated once alongside the chart rather than repeated on every tick.
 */
export function formatRunLabel(createdAt: string | undefined): string {
  return createdAt ? moment(createdAt).format('MMM D h:mma') : '-';
}

/** Last 12 characters of a run's id, enough to identify it without showing a full uuid. */
export function formatRunId(jobId: string): string {
  return jobId.slice(-12);
}

/**
 * A rule's run history as sparkline points, oldest to newest.
 *
 * The API returns runs newest first while Sparkline expects chronological order, so they are
 * reversed. A rule with a single run is duplicated a second later, since one point draws no line.
 */
export function getRunHistoryPoints(runs: ValidationStat[]): SparklinePoint[] {
  const points = [...runs].reverse().map((run) => ({ date: run.runTime, value: run.failedItems }));
  if (points.length === 1) {
    const [onlyRun] = points;
    return [onlyRun, { ...onlyRun, date: moment(onlyRun.date).add(1, 'second').toISOString() }];
  }
  return points;
}

/** Item-locations a rule selected on its most recent run. Runs are newest first. */
export function getItemsSelectedLastRun(runs: ValidationStat[]): number | undefined {
  return runs[0]?.failedItems;
}

/**
 * The runs either side of the open one, for stepping between them in the modal.
 *
 * Runs are newest first, so the previous (older) run is the next index and the next (newer) run is
 * the one before. Both are undefined at the edges of what the run table has loaded — stepping past
 * them would need another page of runs.
 */
export function getAdjacentRunIds(runIds: string[], runId: string): { previousRunId?: string; nextRunId?: string } {
  const index = runIds.indexOf(runId);
  if (index === -1) return {};
  return {
    previousRunId: runIds[index + 1],
    nextRunId: index > 0 ? runIds[index - 1] : undefined,
  };
}
